import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, ChevronDown, Clock3, RotateCcw, Loader2 } from "lucide-react";
import { useApiData } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { KartaPrehledu, HlavickaDlazdice, Pilulka } from "./PrehledDlazdice.jsx";

// Notifikace z Operations úkolů pro CSM a managera (17.7.2026).
// STEJNÁ LOGIKA jako ownerův zvoneček (taby Aktivní / Odložené / Archiv,
// odložit na X dní, archivovat, vrátit) — ale JEN nad jejich vlastními
// notifikacemi. Zdroje jsou odvozené z úkolů:
//  🔍 úkoly čekající na MOJI kontrolu (jsem zadavatel)
//  ⚠️ MOJE úkoly po deadlinu
//  🆕 nové úkoly přiřazené mně (posledních 72 h)
//  ⏳ (owner) rutiny čekající na schválení
// Stav (odloženo/archiv) se drží per uživatel v localStorage — derivované
// notifikace nemají backend, zmizí samy, když pomine důvod.

const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
function jeToJa(jmeno, ja) {
  const p = norm(jmeno), n = norm(ja);
  if (!p || !n) return false;
  if (p === n || p.includes(n) || n.includes(p)) return true;
  return p.split(/\s+/)[0] === n.split(/\s+/)[0];
}
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// --- Perzistence stavu (odloženo/archiv) v localStorage per uživatel ---
const stavKey = () => `fw40_ops_notif_stav_${norm(getCurrentUser()?.email || "anon")}`;
function loadStav() {
  try { return JSON.parse(localStorage.getItem(stavKey()) || "{}"); } catch { return {}; }
}
function saveStav(map) {
  try { localStorage.setItem(stavKey(), JSON.stringify(map)); } catch { /* private mode */ }
}

// Hook: syrové notifikace (bez stavu).
// roleOverride: společný zvoneček manažera, který trénuje (#186), načítá manažerské
// notifikace i ve chvíli, kdy je přepnutý do role Trenér.
export function useOperationsNotifikace(roleOverride = "") {
  const role = String(roleOverride || getCurrentUser()?.role || "").toLowerCase();
  const ja = getCurrentUser()?.name || "";
  const enabled = ["manager", "csm", "owner"].includes(role);
  const { data } = useApiData(enabled ? "/api/operations-ukoly" : null);

  return useMemo(() => {
    const list = [];
    const tasks = (data && data.tasks) || [];
    const t0 = todayStr();
    const otevrene = tasks.filter((t) => !["hotovo", "archiv"].includes(t.status));

    for (const t of otevrene.filter((t) => t.ke_kontrole_kdy && jeToJa(t.zadavatel, ja))) {
      list.push({ id: `kontrola-${t.id}`, ikona: "🔍", title: `Čeká na tvoji kontrolu: ${t.nazev}`, detail: `odevzdal(a) ${t.ke_kontrole_od || t.pilot}`, link: "/operations/ukoly" });
    }
    for (const t of otevrene.filter((t) => jeToJa(t.pilot, ja) && t.deadline && String(t.deadline).slice(0, 10) < t0)
      .sort((a, b) => String(a.deadline).localeCompare(String(b.deadline)))) {
      list.push({ id: `deadline-${t.id}`, ikona: "⚠️", title: `Po deadlinu: ${t.nazev}`, detail: `termín ${t.deadline.slice(8, 10)}. ${Number(t.deadline.slice(5, 7))}.`, link: "/operations/ukoly" });
    }
    const limit = Date.now() - 72 * 60 * 60 * 1000;
    for (const t of otevrene.filter((t) => jeToJa(t.pilot, ja) && !jeToJa(t.zadavatel, ja) && t.created_at && new Date(t.created_at).getTime() > limit)) {
      list.push({ id: `novy-${t.id}`, ikona: "🆕", title: `Nový úkol: ${t.nazev}`, detail: `zadal(a) ${t.zadavatel || "?"}`, link: "/operations/ukoly" });
    }
    if (role === "owner") {
      for (const r of (data?.rutiny || []).filter((r) => r.schvaleno === false)) {
        list.push({ id: `rutina-${r.id}`, ikona: "⏳", title: `Rutina čeká na schválení: ${r.nazev}`, detail: `navrhl(a) ${r.navrhl || "?"}`, link: "/operations/ukoly" });
      }
      // Žádosti o odklad úkolu (schvaluje owner v meetingu nebo na boardu).
      for (const t of otevrene.filter((t) => t.odklad_zadost)) {
        list.push({ id: `odklad-${t.id}`, ikona: "⏳", title: `Žádost o odklad: ${t.nazev}`, detail: `${t.odklad_od || "?"}: ${t.odklad_zadost}`, link: "/operations/ukoly" });
      }
    }
    return list;
  }, [data, ja, role]);
}

// Hook se stavem à la ownerův přehled: každá notifikace má stav
// aktivní / odloženo (do data) / archivováno + akce archive/snooze/restore.
export function useOperationsNotifikaceSeStavem(extra = [], roleOverride = "") {
  const zaklad = useOperationsNotifikace(roleOverride);
  const [stavMap, setStavMap] = useState(loadStav);

  const notifikace = useMemo(() => {
    const now = Date.now();
    return [...zaklad, ...extra].map((n) => {
      const st = stavMap[n.id];
      let stav = "aktivní";
      if (st?.stav === "archivováno") stav = "archivováno";
      else if (st?.stav === "odloženo") stav = st.do && st.do > now ? "odloženo" : "aktivní";
      return { ...n, stav, odlozenoDo: st?.do || null };
    });
  }, [zaklad, extra, stavMap]);

  const uloz = useCallback((id, zaznam) => {
    setStavMap((cur) => {
      const next = { ...cur };
      if (zaznam) next[id] = zaznam; else delete next[id];
      saveStav(next);
      return next;
    });
  }, []);

  return {
    notifikace,
    counts: {
      "aktivní": notifikace.filter((n) => n.stav === "aktivní").length,
      "odloženo": notifikace.filter((n) => n.stav === "odloženo").length,
      "archivováno": notifikace.filter((n) => n.stav === "archivováno").length,
    },
    archive: (id) => uloz(id, { stav: "archivováno" }),
    snooze: (id, dny = 1) => uloz(id, { stav: "odloženo", do: Date.now() + dny * 24 * 60 * 60 * 1000 }),
    restore: (id) => uloz(id, null),
  };
}

// Tělo panelu (taby + seznam + akce) — používá zvoneček (ManagerBell) i dashboard.
// dalsiAktivni / dalsiPocet: notifikace druhé role ve společném zvonečku (#186), kreslí se
// na konci záložky Aktivní a počítají se do jejího čísla.
export function NotifikacePanelBody({ stav, kompakt = false, onNavigate, prejdi = null, dalsiAktivni = null, dalsiPocet = 0 }) {
  const navigate = useNavigate();
  const { notifikace, counts, archive, snooze, restore } = stav;
  const [tab, setTab] = useState("aktivní");
  const [vybrane, setVybrane] = useState(() => new Set());
  const [hromadneOdlozit, setHromadneOdlozit] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [errId, setErrId] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const visible = notifikace.filter((n) => n.stav === tab);
  // prejdi: vlastní přechod (společný zvoneček v roli Trenér nejdřív přepne roli zpět na Manager).
  const go = (link) => { onNavigate?.(); if (prejdi) prejdi(link || "/operations/ukoly"); else navigate(link || "/operations/ukoly"); };

  const prepniTab = (k) => { setTab(k); setVybrane(new Set()); };
  const prepniVyber = (id) => setVybrane((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const vseVybrano = visible.length > 0 && visible.every((n) => vybrane.has(n.id));
  const prepniVse = () => setVybrane(vseVybrano ? new Set() : new Set(visible.map((n) => n.id)));
  const hromadneVyrizeno = () => { [...vybrane].forEach((id) => archive(id)); setVybrane(new Set()); };
  const hromadneNaDni = (dny) => { [...vybrane].forEach((id) => snooze(id, dny)); setVybrane(new Set()); setHromadneOdlozit(false); };

  const oznacHotovo = async (n) => {
    if (busyId) return;
    setBusyId(n.id); setErrId(null); setErrMsg("");
    try {
      await n.hotovo();
      archive(n.id); // schovej z aktivnich (po refetchi ukolu zmizi uplne)
    } catch (e) {
      setErrId(n.id); setErrMsg(e?.message || "Nepodarilo se oznacit jako hotove.");
    } finally {
      setBusyId(null);
    }
  };

  const TABY = [
    { key: "aktivní", label: "Aktivní", count: counts["aktivní"] + dalsiPocet },
    { key: "odloženo", label: "Odložené", count: counts["odloženo"] },
    { key: "archivováno", label: "Archiv", count: counts["archivováno"] },
  ];

  return (
    <>
      {/* 12.9.2026: záložky, hromadný výběr i akce v řádku jsou stejné jako
          v ownerově kartě Notifikace (components/NotificationsPanel.jsx).
          Manager a CSM vidí tentýž panel, jen nad svými notifikacemi. */}
      <div className="px-4 py-3 sm:px-5">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
          {TABY.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => prepniTab(t.key)}
              className={`min-h-[44px] min-w-0 flex-1 rounded-lg px-2 py-1.5 text-sm transition sm:min-h-0 ${
                tab === t.key ? "bg-white font-semibold text-slate-900 shadow-sm" : "text-slate-500 sm:hover:text-slate-700"
              }`}
            >
              <span className="truncate">{t.label}</span>
              {t.count > 0 && <span className="ml-1 tabular-nums">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Hromadný výběr. Ve zvonečku se nekreslí, panel je úzký. */}
      {!kompakt && visible.length > 0 && (tab === "aktivní" || tab === "odloženo") && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-y border-slate-200 bg-slate-50 px-4 py-2 sm:px-5">
          <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs font-semibold text-slate-600">
            <input type="checkbox" checked={vseVybrano} onChange={prepniVse} className="h-4 w-4 accent-brand" />
            {vybrane.size > 0 ? `${vybrane.size} vybráno` : "Vybrat vše"}
          </label>
          {vybrane.size > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={hromadneVyrizeno} className="btn btn-primary">
                <Check className="h-3.5 w-3.5" /> Vyřízeno ({vybrane.size})
              </button>
              <div className="relative">
                <button onClick={() => setHromadneOdlozit((o) => !o)} className="btn btn-secondary">
                  <Clock3 className="h-3.5 w-3.5" /> Odložit ({vybrane.size})
                  <ChevronDown className="h-3 w-3" />
                </button>
                {hromadneOdlozit && (
                  <div className="absolute right-0 top-full z-10 mt-1 min-w-[120px] rounded-xl border border-slate-200 bg-white shadow-lg">
                    {[{ d: 1, l: "zítra" }, { d: 3, l: "3 dny" }, { d: 7, l: "7 dní" }].map((o) => (
                      <button key={o.d} onClick={() => hromadneNaDni(o.d)}
                        className="block w-full px-3 py-2 text-left text-xs sm:hover:bg-slate-50">
                        {o.l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setVybrane(new Set())} className="btn btn-ghost">Zrušit výběr</button>
            </div>
          )}
        </div>
      )}

      <div className={kompakt ? "max-h-[60vh] overflow-y-auto overscroll-contain" : ""}>
        {visible.length === 0 && !(tab === "aktivní" && dalsiPocet > 0) ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500 sm:px-5">
            {tab === "aktivní" && "Žádné aktivní notifikace, vše pod kontrolou."}
            {tab === "odloženo" && "Žádné odložené notifikace."}
            {tab === "archivováno" && "Archiv je prázdný."}
          </div>
        ) : (
          <div className="divide-y divide-slate-200 px-4 sm:px-5">
            {visible.map((n) => (
              <RadekNotifikace
                key={n.id}
                n={n}
                kompakt={kompakt}
                busy={busyId === n.id}
                chyba={errId === n.id ? errMsg : ""}
                vybran={vybrane.has(n.id)}
                onVyber={!kompakt && (tab === "aktivní" || tab === "odloženo") ? prepniVyber : undefined}
                onOtevri={() => go(n.link)}
                onHotovo={() => oznacHotovo(n)}
                onVyrizeno={() => archive(n.id)}
                onOdloz={(dny) => snooze(n.id, dny)}
                onVrat={() => restore(n.id)}
              />
            ))}
          </div>
        )}
        {tab === "aktivní" && dalsiPocet > 0 && dalsiAktivni}
      </div>
    </>
  );
}

// Jeden řádek notifikace. Akce jsou tlačítka s popiskem, ne samotné ikonky:
// na telefonu se do ikonek špatně trefuje a nebylo poznat, co dělají.
function RadekNotifikace({ n, kompakt, busy, chyba, vybran, onVyber, onOtevri, onHotovo, onVyrizeno, onOdloz, onVrat }) {
  const [odlozitOtevren, setOdlozitOtevren] = useState(false);
  const akce = [];
  if (n.stav === "aktivní") {
    akce.push(
      <button key="vyrizeno" onClick={n.hotovo ? onHotovo : onVyrizeno} disabled={busy}
        className="btn btn-primary flex-1 sm:flex-none" title={n.hotovo ? "Označit úkol jako hotový" : "Označit jako vyřízené"}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        {n.hotovo ? "Hotovo" : "Vyřízeno"}
      </button>,
      <div key="odlozit" className="relative flex-1 sm:flex-none">
        <button onClick={() => setOdlozitOtevren((o) => !o)} className="btn btn-secondary w-full" title="Odložit na později">
          <Clock3 className="h-3.5 w-3.5" /> Odložit
          <ChevronDown className="h-3 w-3" />
        </button>
        {odlozitOtevren && (
          <div className="absolute right-0 top-full z-10 mt-1 min-w-[120px] rounded-xl border border-slate-200 bg-white shadow-lg">
            {[{ d: 1, l: "zítra" }, { d: 3, l: "3 dny" }, { d: 7, l: "7 dní" }].map((o) => (
              <button key={o.d} onClick={() => { setOdlozitOtevren(false); onOdloz(o.d); }}
                className="block w-full px-3 py-2 text-left text-xs sm:hover:bg-slate-50">
                {o.l}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (n.stav === "odloženo") {
    akce.push(
      <button key="aktivovat" onClick={onVrat} className="btn btn-secondary flex-1 sm:flex-none" title="Aktivovat teď">
        <RotateCcw className="h-3.5 w-3.5" /> Aktivovat teď
      </button>,
      <button key="vyrizeno" onClick={onVyrizeno} className="btn btn-primary flex-1 sm:flex-none" title="Označit jako vyřízené">
        <Check className="h-3.5 w-3.5" /> Vyřízeno
      </button>
    );
  }
  if (n.stav === "archivováno") {
    akce.push(
      <button key="obnovit" onClick={onVrat} className="btn btn-secondary flex-1 sm:flex-none" title="Vrátit mezi aktivní">
        <RotateCcw className="h-3.5 w-3.5" /> Obnovit
      </button>
    );
  }

  return (
    <div className={`py-3 ${vybran ? "bg-brand-light/50" : ""}`}>
      <div className="flex items-start gap-2.5">
        {onVyber && (
          <input type="checkbox" checked={vybran} onChange={() => onVyber(n.id)}
            className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-brand" title="Vybrat pro hromadnou akci" />
        )}
        <span className="mt-0.5 shrink-0 text-lg leading-none">{n.ikona}</span>
        <div className="min-w-0 flex-1">
          <button type="button" onClick={onOtevri} className="block w-full text-left">
            <span className="block font-semibold leading-snug text-slate-900">{n.title}</span>
            <span className="mt-0.5 block text-sm leading-snug text-slate-600 demo-sensitive">{n.detail}</span>
          </button>
          {n.stav === "odloženo" && n.odlozenoDo && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <Pilulka>odloženo do {new Date(n.odlozenoDo).toLocaleDateString("cs-CZ")}</Pilulka>
            </div>
          )}
          {akce.length > 0 && <div className={`mt-2.5 flex gap-2 ${kompakt ? "flex-wrap" : ""}`}>{akce}</div>}
          {chyba && <div className="mt-1.5 text-xs text-rose-600">{chyba}</div>}
        </div>
      </div>
    </div>
  );
}

// Karta notifikací pro dashboardy (CSM, manager). Stejná stavba jako ownerova
// karta Notifikace, jen nad jejich vlastními notifikacemi z úkolů.
export function NotifikacePrehled() {
  const stav = useOperationsNotifikaceSeStavem();
  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Bell}
        nazev="Notifikace"
        odznak={`${stav.counts["aktivní"] || 0} aktivních`}
        odznakZivy
      />
      <NotifikacePanelBody stav={stav} />
    </KartaPrehledu>
  );
}
